import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';

// ---------- helpers ----------

function removerAcentos(str) {
  return str
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '');
}

function calcularLoginSugerido(nomeFantasia, cnpj) {
  const primeiroNome = (nomeFantasia || '').trim().split(/\s+/)[0];
  const cnpjLimpo = (cnpj || '').replace(/\D/g, '');
  const sufixo = cnpjLimpo.slice(-5);
  return removerAcentos(primeiroNome).toLowerCase() + sufixo;
}

function aplicarMascaraCNPJ(valor) {
  const s = valor.replace(/\D/g, '').slice(0, 14);
  return s
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

// ---------- sub-componentes ----------

function Secao({ titulo, descricao, children }) {
  return (
    <div className="card mb-6">
      <h2 className="section-title">{titulo}</h2>
      {descricao && <p className="section-sub">{descricao}</p>}
      {children}
    </div>
  );
}

function ModalCredenciais({ dados, onEnviarEmail, onIr, onFechar }) {
  const [emailDestino, setEmailDestino] = useState(dados.emailCliente || '');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [erroEmail, setErroEmail] = useState('');

  function copiar(texto) {
    navigator.clipboard.writeText(texto).catch(() => {});
  }

  async function handleEnviarEmail() {
    if (!emailDestino) {
      setErroEmail('Informe o e-mail do destinatário.');
      return;
    }
    setErroEmail('');
    setEnviando(true);
    try {
      await onEnviarEmail(emailDestino);
      setEnviado(true);
    } catch {
      setErroEmail('Erro ao enviar e-mail. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900">Onboarding criado!</h2>
          <p className="text-sm text-gray-500 mt-1">
            Guarde as credenciais abaixo para enviar ao cliente.
          </p>
        </div>

        <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 space-y-3 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-brand-600 font-medium uppercase tracking-wide">Login</p>
              <p className="text-base font-bold text-brand-900 font-mono">{dados.login}</p>
            </div>
            <button
              onClick={() => copiar(dados.login)}
              className="btn-ghost text-xs px-2 py-1"
              title="Copiar login"
            >
              Copiar
            </button>
          </div>
          <div className="border-t border-brand-200" />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-brand-600 font-medium uppercase tracking-wide">Senha</p>
              <p className="text-base font-bold text-brand-900 font-mono">{dados.senha}</p>
            </div>
            <button
              onClick={() => copiar(dados.senha)}
              className="btn-ghost text-xs px-2 py-1"
              title="Copiar senha"
            >
              Copiar
            </button>
          </div>
        </div>

        {/* Enviar e-mail */}
        <div className="mb-5">
          <label className="label">E-mail do cliente para envio</label>
          <div className="flex gap-2">
            <input
              type="email"
              value={emailDestino}
              onChange={e => { setEmailDestino(e.target.value); setErroEmail(''); }}
              className="input"
              placeholder="email@empresa.com.br"
              disabled={enviado}
            />
            <button
              onClick={handleEnviarEmail}
              disabled={enviando || enviado}
              className={`btn-secondary whitespace-nowrap ${enviado ? 'opacity-60 cursor-default' : ''}`}
            >
              {enviado ? 'Enviado!' : enviando ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
          {erroEmail && (
            <p className="text-xs text-red-600 mt-1">{erroEmail}</p>
          )}
          {enviado && (
            <p className="text-xs text-green-600 mt-1">E-mail enviado com sucesso.</p>
          )}
        </div>

        <div className="flex gap-2">
          <button onClick={onFechar} className="btn-ghost flex-1 justify-center">
            Fechar
          </button>
          <button onClick={onIr} className="btn-primary flex-1 justify-center">
            Ir para o onboarding
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- página principal ----------

export default function NovoOnboarding() {
  const nav = useNavigate();

  // Seção 1 — dados da empresa
  const [razaoSocial, setRazaoSocial] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [dataEntrada, setDataEntrada] = useState('');
  const [responsavelFinanceiro, setResponsavelFinanceiro] = useState('');
  const [nomeAdministrador, setNomeAdministrador] = useState('');

  // Seção 2 — módulos
  const [modulos, setModulos] = useState([]);
  const [modulosSelecionados, setModulosSelecionados] = useState([]);
  const [loadingModulos, setLoadingModulos] = useState(true);

  // Seção 3 — empresas filiais
  const [empresas, setEmpresas] = useState([]);

  // Seção 4 — anexos extras
  const [anexosExtras, setAnexosExtras] = useState([]);

  // Seção 5 — credenciais
  const [loginSugerido, setLoginSugerido] = useState('');
  const [senhaSugerida, setSenhaSugerida] = useState('');
  const [emailCliente, setEmailCliente] = useState('');

  // estado geral
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [modalDados, setModalDados] = useState(null); // { id, login, senha, emailCliente }

  // Calcular login/senha sugeridos automaticamente
  const recalcularLogin = useCallback(() => {
    const sugerido = calcularLoginSugerido(nomeFantasia, cnpj);
    setLoginSugerido(sugerido);
    setSenhaSugerida(sugerido);
  }, [nomeFantasia, cnpj]);

  useEffect(() => {
    recalcularLogin();
  }, [recalcularLogin]);

  useEffect(() => {
    async function buscarModulos() {
      try {
        const { data } = await api.get('/modulos');
        setModulos(data.filter(m => m.ativo !== false));
      } catch {
        // não bloqueia o formulário
      } finally {
        setLoadingModulos(false);
      }
    }
    buscarModulos();
  }, []);

  // --- helpers empresas filiais ---
  function adicionarEmpresa() {
    setEmpresas(prev => [...prev, { cnpj: '', nome: '', descricao: '' }]);
  }
  function removerEmpresa(idx) {
    setEmpresas(prev => prev.filter((_, i) => i !== idx));
  }
  function atualizarEmpresa(idx, campo, valor) {
    setEmpresas(prev => prev.map((e, i) => i === idx ? { ...e, [campo]: valor } : e));
  }

  // --- helpers anexos extras ---
  function adicionarAnexo() {
    setAnexosExtras(prev => [...prev, { label: '' }]);
  }
  function removerAnexo(idx) {
    setAnexosExtras(prev => prev.filter((_, i) => i !== idx));
  }
  function atualizarAnexo(idx, valor) {
    setAnexosExtras(prev => prev.map((a, i) => i === idx ? { label: valor } : a));
  }

  // --- toggle módulo ---
  function toggleModulo(id) {
    setModulosSelecionados(prev =>
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  }

  // --- submit ---
  async function handleSubmit(e) {
    e.preventDefault();
    setErro('');
    setSalvando(true);

    const payload = {
      razaoSocial,
      nomeFantasia,
      cnpj: cnpj.replace(/\D/g, ''),
      dataEntrada,
      responsavelFinanceiro,
      nomeAdministrador,
      modulos: modulosSelecionados,
      empresas,
      anexosExtras,
      login: loginSugerido,
      senha: senhaSugerida,
      emailCliente: emailCliente || undefined,
    };

    try {
      const { data } = await api.post('/admin/onboardings', payload);
      setModalDados({
        id: data.id,
        login: data.login,
        senha: data.senha,
        emailCliente: emailCliente || '',
      });
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao criar onboarding. Verifique os dados e tente novamente.');
    } finally {
      setSalvando(false);
    }
  }

  async function handleEnviarEmail(emailDestino) {
    await api.post(`/admin/onboardings/${modalDados.id}/enviar-email`, {
      emailDestinatario: emailDestino,
      senha: modalDados.senha,
    });
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-20">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <img src="/logo_ht.png" alt="HeadThink" className="h-9 w-auto" />
            <div className="h-6 w-px bg-gray-200" />
            <div>
              <p className="text-xs text-gray-400 leading-none">Painel Administrativo!!</p>
              <p className="text-sm font-semibold text-gray-800 leading-tight">Novo Onboarding</p>
            </div>
          </div>
          <Link to="/admin" className="btn-ghost text-xs">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Voltar
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-0">

          {/* Seção 1 — Dados da empresa */}
          <Secao titulo="Dados da Empresa" descricao="Informações principais do cliente.">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="label">Razão Social *</label>
                <input
                  type="text"
                  value={razaoSocial}
                  onChange={e => setRazaoSocial(e.target.value)}
                  className="input"
                  placeholder="Razão social completa"
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <label className="label">Nome Fantasia *</label>
                <input
                  type="text"
                  value={nomeFantasia}
                  onChange={e => setNomeFantasia(e.target.value)}
                  className="input"
                  placeholder="Nome fantasia"
                  required
                />
              </div>
              <div>
                <label className="label">CNPJ *</label>
                <input
                  type="text"
                  value={cnpj}
                  onChange={e => setCnpj(aplicarMascaraCNPJ(e.target.value))}
                  className="input font-mono"
                  placeholder="XX.XXX.XXX/XXXX-XX"
                  maxLength={18}
                  required
                />
              </div>
              <div>
                <label className="label">Data de Entrada *</label>
                <input
                  type="date"
                  value={dataEntrada}
                  onChange={e => setDataEntrada(e.target.value)}
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="label">Responsável Financeiro *</label>
                <input
                  type="text"
                  value={responsavelFinanceiro}
                  onChange={e => setResponsavelFinanceiro(e.target.value)}
                  className="input"
                  placeholder="Nome do responsável financeiro"
                  required
                />
              </div>
              <div>
                <label className="label">Nome do Administrador do Sistema *</label>
                <input
                  type="text"
                  value={nomeAdministrador}
                  onChange={e => setNomeAdministrador(e.target.value)}
                  className="input"
                  placeholder="Nome do administrador"
                  required
                />
              </div>
            </div>
          </Secao>

          {/* Seção 2 — Módulos */}
          <Secao titulo="Módulos Contratados" descricao="Selecione os módulos que o cliente contratou.">
            {loadingModulos ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-10 bg-gray-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : modulos.length === 0 ? (
              <p className="text-sm text-gray-400">Nenhum módulo cadastrado ainda.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {modulos.map(m => {
                  const marcado = modulosSelecionados.includes(m.id);
                  return (
                    <label
                      key={m.id}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-all text-sm ${
                        marcado
                          ? 'border-brand-500 bg-brand-50 text-brand-800 font-medium'
                          : 'border-gray-200 text-gray-700 hover:border-brand-300 hover:bg-brand-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={marcado}
                        onChange={() => toggleModulo(m.id)}
                        className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-400"
                      />
                      {m.nome}
                    </label>
                  );
                })}
              </div>
            )}
          </Secao>

          {/* Seção 3 — Empresas filiais */}
          <Secao titulo="Empresas (CNPJs Filiais)" descricao="Adicione as filiais ou empresas do grupo, se houver.">
            <div className="space-y-3">
              {empresas.map((emp, idx) => (
                <div key={idx} className="border border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Empresa {idx + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => removerEmpresa(idx)}
                      className="btn-danger text-xs px-2 py-1"
                    >
                      Remover
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="label">CNPJ</label>
                      <input
                        type="text"
                        value={emp.cnpj}
                        onChange={e => atualizarEmpresa(idx, 'cnpj', aplicarMascaraCNPJ(e.target.value))}
                        className="input font-mono"
                        placeholder="XX.XXX.XXX/XXXX-XX"
                        maxLength={18}
                      />
                    </div>
                    <div>
                      <label className="label">Nome *</label>
                      <input
                        type="text"
                        value={emp.nome}
                        onChange={e => atualizarEmpresa(idx, 'nome', e.target.value)}
                        className="input"
                        placeholder="Nome da empresa"
                        required
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="label">Descrição</label>
                      <textarea
                        value={emp.descricao}
                        onChange={e => atualizarEmpresa(idx, 'descricao', e.target.value)}
                        className="input resize-none"
                        rows={2}
                        placeholder="Observação ou descrição (opcional)"
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={adicionarEmpresa}
                className="btn-secondary w-full justify-center"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Adicionar Empresa
              </button>
            </div>
          </Secao>

          {/* Seção 4 — Anexos extras */}
          <Secao titulo="Anexos Extras" descricao="Tipos de anexo adicionais além dos padrão.">
            <div className="space-y-2">
              {anexosExtras.map((a, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={a.label}
                    onChange={e => atualizarAnexo(idx, e.target.value)}
                    className="input"
                    placeholder="Ex: Contrato Social Atualizado"
                  />
                  <button
                    type="button"
                    onClick={() => removerAnexo(idx)}
                    className="btn-danger shrink-0 px-2 py-2"
                    title="Remover"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={adicionarAnexo}
                className="btn-secondary w-full justify-center"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Adicionar Tipo de Anexo
              </button>
            </div>
          </Secao>

          {/* Seção 5 — Credenciais */}
          <Secao titulo="Credenciais de Acesso" descricao="Login e senha que serão criados para o cliente.">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="label">Login</label>
                <input
                  type="text"
                  value={loginSugerido}
                  onChange={e => {
                    setLoginSugerido(e.target.value);
                    setSenhaSugerida(e.target.value);
                  }}
                  className="input font-mono"
                  placeholder="Login gerado automaticamente"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Gerado a partir do nome fantasia + últimos 5 dígitos do CNPJ.
                </p>
              </div>
              <div>
                <label className="label">Senha</label>
                <input
                  type="text"
                  value={senhaSugerida}
                  onChange={e => setSenhaSugerida(e.target.value)}
                  className="input font-mono"
                  placeholder="Senha inicial"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="label">E-mail do cliente (para envio posterior)</label>
                <input
                  type="email"
                  value={emailCliente}
                  onChange={e => setEmailCliente(e.target.value)}
                  className="input"
                  placeholder="email@empresa.com.br (opcional)"
                />
              </div>
            </div>

            {/* Card de preview das credenciais */}
            {(loginSugerido || senhaSugerida) && (
              <div className="bg-brand-50 border border-brand-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-brand-700 uppercase tracking-wide mb-3">
                  Credenciais que serão criadas
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-brand-500">Login</p>
                    <p className="font-mono font-bold text-brand-900">{loginSugerido || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-brand-500">Senha</p>
                    <p className="font-mono font-bold text-brand-900">{senhaSugerida || '—'}</p>
                  </div>
                </div>
              </div>
            )}
          </Secao>

          {/* Erro geral */}
          {erro && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg mb-4">
              <p className="text-sm text-red-700">{erro}</p>
            </div>
          )}

          {/* Ações */}
          <div className="flex justify-end gap-3 pt-2 pb-12">
            <Link to="/admin" className="btn-ghost">
              Cancelar
            </Link>
            <button type="submit" disabled={salvando} className="btn-primary">
              {salvando ? 'Criando...' : 'Criar Onboarding'}
            </button>
          </div>
        </form>
      </main>

      {/* Modal credenciais */}
      {modalDados && (
        <ModalCredenciais
          dados={modalDados}
          onEnviarEmail={handleEnviarEmail}
          onIr={() => nav(`/admin/onboarding/${modalDados.id}`)}
          onFechar={() => nav('/admin')}
        />
      )}
    </div>
  );
}
